import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import useLanguage from '../../hooks/useLanguage';

/**
 * The unit's guest-guide link, at the top of «دليل العقار».
 * tasks/guest-guide-sections-and-print-sheet.md (Phase 2, G13).
 *
 * The link is the QR code on the printed sheet in the apartment, so «رابط جديد» is an exceptional
 * action: it needs an explicit tick acknowledging the printed QR will stop working. A browser
 * `confirm()` was too easy to click through while troubleshooting.
 *
 * «طباعة» opens the same guide in print mode (`#<token>&print`) — an A4 preview with the logo and a
 * QR to this link; ops press Print there (Phase 4, G7).
 *
 * @param {string|null} link        - the current guide URL, or null while the unit has none
 * @param {() => Promise<void>} onRegenerate - rotates the token; throws on failure
 */
const GuestGuideLinkCard = ({ link, onRegenerate }) => {
  const { t, isArabic } = useLanguage();
  const [showModal, setShowModal] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [working, setWorking] = useState(false);
  const [notice, setNotice] = useState(null);

  const closeModal = () => {
    setShowModal(false);
    setAcknowledged(false);
  };

  const copy = () => {
    // `writeText` needs a secure context; local HTTP dev has none, so the field stays selectable
    // and the failure says so instead of silently copying nothing.
    navigator.clipboard?.writeText(link)
      .then(() => setNotice({ ok: true, text: t('guestGuide.unit.copied') }))
      .catch(() => setNotice({ ok: false, text: t('guestGuide.unit.copyFailed') }));
  };

  const regenerate = async () => {
    setWorking(true);
    try {
      await onRegenerate();
      closeModal();
    } catch (error) {
      console.error('Guest guide link regenerate failed:', error);
      setNotice({ ok: false, text: t('guestGuide.unit.newLinkFailed') });
      closeModal();
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="card p-3 mb-4" style={{ borderInlineStart: '3px solid #471396' }}>
      <h5 className="mb-2">{t('guestGuide.unit.guestLink')}</h5>

      {link ? (
        <>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <input
              type="text"
              className="form-control"
              readOnly
              value={link}
              aria-label={t('guestGuide.unit.guestLink')}
              onFocus={(e) => e.target.select()}
              style={{ direction: 'ltr', textAlign: 'left', maxWidth: '360px' }}
            />
            <button type="button" className="btn btn-outline-secondary" onClick={copy}>
              <i className="bi bi-clipboard" /> {t('guestGuide.unit.copy')}
            </button>
            <a className="btn btn-outline-primary" href={link} target="_blank" rel="noopener noreferrer">
              <i className="bi bi-box-arrow-up-left" /> {t('guestGuide.unit.preview')}
            </a>
            <a className="btn btn-outline-primary" href={`${link}&print`} target="_blank" rel="noopener noreferrer">
              <i className="bi bi-printer" /> {t('guestGuide.unit.print')}
            </a>
            <button type="button" className="btn btn-outline-danger" onClick={() => setShowModal(true)}>
              {t('guestGuide.unit.newLink')}
            </button>
          </div>
          {notice && (
            <small className={`d-block mt-1 ${notice.ok ? 'text-success' : 'text-danger'}`}>{notice.text}</small>
          )}
          <small className="text-muted d-block mt-1">{t('guestGuide.unit.guestLinkHint')}</small>
        </>
      ) : (
        <p className="text-muted small mb-0">{t('guestGuide.unit.noLinkYet')}</p>
      )}

      <Modal show={showModal} onHide={closeModal} centered dir={isArabic ? 'rtl' : 'ltr'}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">{t('guestGuide.unit.newLinkTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ textAlign: isArabic ? 'right' : 'left' }}>
          <p>{t('guestGuide.unit.newLinkBody')}</p>
          <p className="text-muted small">{t('guestGuide.unit.newLinkUse')}</p>
          <Form.Check
            type="checkbox"
            id="guest-guide-new-link-ack"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            label={t('guestGuide.unit.newLinkConfirm')}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeModal} disabled={working}>
            {t('guestGuide.unit.cancel')}
          </Button>
          <Button variant="danger" onClick={regenerate} disabled={!acknowledged || working}>
            {t('guestGuide.unit.createNewLink')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GuestGuideLinkCard;
